/**
 * IntakeChoice — /valuation
 *
 * Entry point between Landing and the actual intake flow. On mount, checks
 * whether conversational (LLM-backed) intake is enabled via
 * GET /lite/intake/config (Task 3).
 *
 *   - enabled: true         -> two visually equal cards: "Have a
 *     conversation" (-> /valuation/chat) and "Fill in the form"
 *     (-> /valuation/form), plus a privacy line about the AI provider.
 *   - enabled: false, OR
 *   - the config request itself fails (network error, 5xx, etc.)
 *     -> fail closed to a single "Start your valuation" CTA straight to
 *     the form. The chat option must never be shown if it might 404 or be
 *     unavailable — /valuation/chat is only wired up in Task 5.
 *
 * Analytics v1 is intentionally minimal and client-side only:
 * localStorage.setItem('intake_path_chosen', 'chat' | 'form') on click.
 * We do NOT thread this into the MVAInput payload sent to the backend —
 * mvaSchema's `meta` object rejects unknown keys, so smuggling
 * `meta.intakePath` through would fail validation. A server-side analytics
 * story (if wanted) is out of scope for this task.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getIntakeConfig } from '../services/api';
import './IntakeChoice.css';

const PRIVACY_LINE =
  'The conversation option is powered by an AI assistant; what you type is processed by our AI provider to prepare your valuation.';

function recordPathChosen(path) {
  try {
    localStorage.setItem('intake_path_chosen', path);
  } catch (err) {
    // localStorage can throw (private browsing, quota exceeded, disabled).
    // This is best-effort analytics only — never block navigation on it.
    console.error('Could not record intake path choice:', err);
  }
}

function IntakeChoice() {
  const [loading, setLoading] = useState(true);
  const [chatEnabled, setChatEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;

    getIntakeConfig()
      .then((config) => {
        if (!cancelled) setChatEnabled(Boolean(config && config.enabled));
      })
      .catch((error) => {
        // Fail closed: any error fetching config means form-only, never a
        // dangling chat link.
        console.error(
          'Could not load intake config; falling back to form-only:',
          error
        );
        if (!cancelled) setChatEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="intake-choice-page">
        <header className="intake-choice-brandbar">
          <span className="intake-choice-wordmark">Exit Ready</span>
        </header>
        <main className="intake-choice-main">
          <p className="intake-choice-loading" role="status">
            Loading&hellip;
          </p>
        </main>
      </div>
    );
  }

  if (!chatEnabled) {
    return (
      <div className="intake-choice-page">
        <header className="intake-choice-brandbar">
          <span className="intake-choice-wordmark">Exit Ready</span>
        </header>
        <main className="intake-choice-main intake-choice-main--solo">
          <h1 className="intake-choice-headline">Let&rsquo;s get started</h1>
          <p className="intake-choice-subheadline">
            A broker-grade estimate across 11 valuation methods, in about 10
            minutes.
          </p>
          <Link
            to="/valuation/form"
            className="intake-choice-solo-cta"
            onClick={() => recordPathChosen('form')}
          >
            Start your valuation
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="intake-choice-page">
      <header className="intake-choice-brandbar">
        <span className="intake-choice-wordmark">Exit Ready</span>
      </header>
      <main className="intake-choice-main">
        <h1 className="intake-choice-headline">
          How would you like to get started?
        </h1>
        <div className="intake-choice-cards">
          <Link
            to="/valuation/chat"
            className="intake-choice-card"
            onClick={() => recordPathChosen('chat')}
          >
            <span className="intake-choice-card-icon" aria-hidden="true">
              &#128172;
            </span>
            <span className="intake-choice-card-title">
              Have a conversation
            </span>
            <span className="intake-choice-card-desc">
              Answer a few questions in plain English, like talking to an
              advisor.
            </span>
          </Link>

          <Link
            to="/valuation/form"
            className="intake-choice-card"
            onClick={() => recordPathChosen('form')}
          >
            <span className="intake-choice-card-icon" aria-hidden="true">
              &#128203;
            </span>
            <span className="intake-choice-card-title">Fill in the form</span>
            <span className="intake-choice-card-desc">
              Step-by-step form, about 10 minutes.
            </span>
          </Link>
        </div>

        <p className="intake-choice-privacy">{PRIVACY_LINE}</p>
      </main>
    </div>
  );
}

export default IntakeChoice;
